import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logReactError } from '../../services/loggingService';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  chunkReloadAttempted: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, chunkReloadAttempted: false };
  }

  private static isChunkLoadError(error: Error): boolean {
    return (
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('error loading dynamically imported module')
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Hvis det er en chunk-feil (typisk etter ny publisering), last siden på nytt automatisk
    if (ErrorBoundary.isChunkLoadError(error)) {
      const reloadKey = 'bizclick_chunk_reload';
      const lastReload = sessionStorage.getItem(reloadKey);

      // Unngå uendelig reload-løkke: kun reload én gang per sesjon
      if (!lastReload) {
        sessionStorage.setItem(reloadKey, Date.now().toString());
        window.location.reload();
        return;
      }

      // Hvis vi allerede har prøvd reload, vis feilmelding i stedet
      this.setState({ chunkReloadAttempted: true });
    }

    // Log the error to our logging service
    logReactError(error, errorInfo);

    // Store error info for display in dev mode
    this.setState({
      error,
      errorInfo
    });

    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  private handleHardReload = () => {
    sessionStorage.removeItem('bizclick_chunk_reload');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error && ErrorBoundary.isChunkLoadError(this.state.error);

      if (isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full mx-4 p-8 bg-white rounded-lg shadow-lg text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-blue-500 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Ny versjon tilgjengelig
                </h1>

                <p className="text-gray-600 mb-4">
                  Det har blitt publisert en ny versjon av applikasjonen. Last siden på nytt for å fortsette.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={this.handleHardReload}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Last inn ny versjon
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full mx-4 p-8 bg-white rounded-lg shadow-lg text-center">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Noe gikk galt
              </h1>

              <p className="text-gray-600 mb-4">
                En uventet feil oppstod i applikasjonen. Feilen har blitt automatisk rapportert.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Last siden på nytt
              </button>

              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Tilbake til forsiden
              </button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg text-left">
                <h3 className="text-sm font-semibold text-red-800 mb-2">
                  Utviklerinfo:
                </h3>
                <div className="text-xs text-red-700 font-mono">
                  <p className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </p>
                  {this.state.errorInfo && (
                    <details>
                      <summary className="cursor-pointer hover:text-red-900">
                        Stack trace
                      </summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap">
                        {this.state.error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
